/**
 * Subscription Plan Service
 *
 * Manages user subscription plans and feature access
 * Plans: Basic (free) and Plus ($4.99/month)
 */

import { supabase, getCurrentUserId } from './supabaseClient';

// Plan types
export const PLANS = {
  BASIC: 'basic',
  PLUS: 'plus'
};

// Plan pricing
export const PLAN_PRICING = {
  [PLANS.BASIC]: {
    name: 'Basic',
    price: 0,
    displayPrice: '$0',
    interval: 'month'
  },
  [PLANS.PLUS]: {
    name: 'Plus',
    price: 4.99,
    displayPrice: '$4.99',
    interval: 'month'
  }
};

// Plan limits
export const PLAN_LIMITS = {
  [PLANS.BASIC]: {
    maxGmailAccounts: 1
  },
  [PLANS.PLUS]: {
    maxGmailAccounts: 5
  }
};

// Feature flags for each plan
export const PLAN_FEATURES = {
  [PLANS.BASIC]: {
    // Dashboard
    canViewMoneySaved: false,

    // Analytics
    canViewPriceChangeImpact: false,
    canViewCurrencyDistribution: false,
    canViewStatusDistribution: false,

    // AI Insights
    canAccessAIInsights: false,

    // Settings
    canUseAdvancedAIClassification: false,

    // Gmail
    maxGmailAccounts: 1,

    // Core features (always available)
    canViewSubscriptions: true,
    canAddSubscriptions: true,
    canEditSubscriptions: true,
    canDeleteSubscriptions: true,
    canViewBasicAnalytics: true,
    canUseCancellationGuides: false,
    canViewEmails: false
  },
  [PLANS.PLUS]: {
    // All features unlocked
    canViewMoneySaved: true,
    canViewPriceChangeImpact: true,
    canViewCurrencyDistribution: true,
    canViewStatusDistribution: true,
    canAccessAIInsights: true,
    canUseAdvancedAIClassification: true,
    maxGmailAccounts: 5,

    // Core features
    canViewSubscriptions: true,
    canAddSubscriptions: true,
    canEditSubscriptions: true,
    canDeleteSubscriptions: true,
    canViewBasicAnalytics: true,
    canUseCancellationGuides: true,
    canViewEmails: true
  }
};

/**
 * Get user's current subscription plan
 * @returns {Promise<string>} Plan type ('basic' or 'plus')
 */
export const getUserPlan = async () => {
  try {
    const userId = await getCurrentUserId();

    const { data, error } = await supabase
      .from('user_profiles')
      .select('subscription_plan, current_period_end, cancel_at_period_end')
      .eq('user_id', userId)
      .single();

    if (error) {
      console.error('Error fetching user plan:', error);
      return PLANS.BASIC; // Default to basic on error
    }

    // Check if subscription has expired and needs to be downgraded
    const hasExpired = data?.current_period_end && new Date(data.current_period_end) < new Date();
    const shouldDowngrade = data?.cancel_at_period_end && hasExpired;

    if (shouldDowngrade && data.subscription_plan === PLANS.PLUS) {
      // Automatically downgrade to Basic
      console.log('⏰ Subscription period expired, downgrading to Basic');
      await supabase
        .from('user_profiles')
        .update({
          subscription_plan: PLANS.BASIC,
          cancel_at_period_end: false,
          subscription_status: 'inactive',
          stripe_subscription_id: null,
          current_period_end: null,
          billing_day_of_month: null,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      return PLANS.BASIC;
    }

    return data?.subscription_plan || PLANS.BASIC;
  } catch (error) {
    console.error('Error in getUserPlan:', error);
    return PLANS.BASIC;
  }
};

/**
 * Update user's subscription plan
 * @param {string} plan - Plan type ('basic' or 'plus')
 * @returns {Promise<boolean>} Success status
 */
export const updateUserPlan = async (plan) => {
  try {
    if (!Object.values(PLANS).includes(plan)) {
      throw new Error(`Invalid plan: ${plan}`);
    }

    const userId = await getCurrentUserId();

    // Get current plan and subscription info
    const { data: currentData } = await supabase
      .from('user_profiles')
      .select('subscription_plan, current_period_end, cancel_at_period_end')
      .eq('user_id', userId)
      .single();

    // Check if this is a downgrade from Plus to Basic
    const isDowngrade = currentData?.subscription_plan === PLANS.PLUS && plan === PLANS.BASIC;
    const hasActivePeriod = currentData?.current_period_end && new Date(currentData.current_period_end) > new Date();

    if (isDowngrade && hasActivePeriod) {
      // Downgrade with grace period: set cancel_at_period_end = true
      // Keep plan as Plus until period ends
      const { error } = await supabase
        .from('user_profiles')
        .update({
          cancel_at_period_end: true,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (error) throw error;
      console.log(`✅ User plan will downgrade to Basic at period end: ${currentData.current_period_end}`);
    } else {
      // Immediate plan change (upgrade or no active period)
      const { error } = await supabase
        .from('user_profiles')
        .update({
          subscription_plan: plan,
          cancel_at_period_end: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', userId);

      if (error) throw error;
      console.log(`✅ User plan updated immediately to: ${plan}`);
    }

    return true;
  } catch (error) {
    console.error('Error updating user plan:', error);
    return false;
  }
};

/**
 * Admin override: Force immediate plan change without grace period
 * ONLY FOR ADMIN USE - bypasses normal subscription flow
 * @param {string} plan - Target plan ('basic' or 'plus')
 * @returns {Promise<boolean>} Success status
 */
export const adminOverridePlan = async (plan) => {
  try {
    if (!Object.values(PLANS).includes(plan)) {
      throw new Error(`Invalid plan: ${plan}`);
    }

    const userId = await getCurrentUserId();
    const now = new Date();

    if (plan === PLANS.BASIC) {
      // Admin downgrade to Basic: Set subscription as expired (1 hour ago)
      const expiredTime = new Date(now.getTime() - 60 * 60 * 1000); // 1 hour before now

      const { error } = await supabase
        .from('user_profiles')
        .update({
          subscription_plan: PLANS.BASIC,
          subscription_status: 'inactive',
          cancel_at_period_end: true,
          current_period_end: expiredTime.toISOString(),
          stripe_subscription_id: null,
          billing_day_of_month: null,
          updated_at: now.toISOString()
        })
        .eq('user_id', userId);

      if (error) throw error;
      console.log('✅ Admin override: User downgraded to Basic (subscription marked as expired)');

    } else if (plan === PLANS.PLUS) {
      // Admin upgrade to Plus: Create fake active subscription
      const periodEnd = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 1 month from now

      const { error } = await supabase
        .from('user_profiles')
        .update({
          subscription_plan: PLANS.PLUS,
          subscription_status: 'active',
          cancel_at_period_end: false,
          current_period_end: periodEnd.toISOString(),
          stripe_subscription_id: 'admin_override',
          billing_day_of_month: now.getDate(),
          billing_interval: 'month',
          updated_at: now.toISOString()
        })
        .eq('user_id', userId);

      if (error) throw error;
      console.log('✅ Admin override: User upgraded to Plus (admin_override subscription)');
    }

    return true;
  } catch (error) {
    console.error('Error in admin plan override:', error);
    return false;
  }
};

/**
 * Check if user has access to a specific feature
 * @param {string} feature - Feature name (e.g., 'canViewMoneySaved')
 * @returns {Promise<boolean>} Whether user has access
 */
export const hasFeatureAccess = async (feature) => {
  try {
    const plan = await getUserPlan();
    const features = PLAN_FEATURES[plan];

    return features?.[feature] ?? false;
  } catch (error) {
    console.error('Error checking feature access:', error);
    return false;
  }
};

/**
 * Get all features for user's current plan
 * @returns {Promise<Object>} Feature flags object
 */
export const getUserFeatures = async () => {
  try {
    const plan = await getUserPlan();
    return PLAN_FEATURES[plan] || PLAN_FEATURES[PLANS.BASIC];
  } catch (error) {
    console.error('Error getting user features:', error);
    return PLAN_FEATURES[PLANS.BASIC];
  }
};

/**
 * Get plan limits for user's current plan
 * @returns {Promise<Object>} Plan limits object
 */
export const getUserPlanLimits = async () => {
  try {
    const plan = await getUserPlan();
    return PLAN_LIMITS[plan] || PLAN_LIMITS[PLANS.BASIC];
  } catch (error) {
    console.error('Error getting plan limits:', error);
    return PLAN_LIMITS[PLANS.BASIC];
  }
};

/**
 * Check if user can add another Gmail account
 * @param {number} currentCount - Current number of Gmail accounts
 * @returns {Promise<{canAdd: boolean, limit: number, current: number}>}
 */
export const canAddGmailAccount = async (currentCount) => {
  try {
    const limits = await getUserPlanLimits();
    const limit = limits.maxGmailAccounts;

    return {
      canAdd: currentCount < limit,
      limit,
      current: currentCount
    };
  } catch (error) {
    console.error('Error checking Gmail account limit:', error);
    return {
      canAdd: false,
      limit: 1,
      current: currentCount
    };
  }
};

/**
 * Get plan pricing information
 * @param {string} plan - Plan type
 * @returns {Object} Pricing information
 */
export const getPlanPricing = (plan) => {
  return PLAN_PRICING[plan] || PLAN_PRICING[PLANS.BASIC];
};

/**
 * Get all available plans with their features
 * @returns {Array} Array of plan objects with features
 */
export const getAllPlans = () => {
  return [
    {
      id: PLANS.BASIC,
      name: PLAN_PRICING[PLANS.BASIC].name,
      price: PLAN_PRICING[PLANS.BASIC].price,
      displayPrice: PLAN_PRICING[PLANS.BASIC].displayPrice,
      interval: PLAN_PRICING[PLANS.BASIC].interval,
      features: [
        'Track unlimited subscriptions',
        'Basic analytics',
        '1 Gmail account sync',
        'Email notifications'
      ]
    },
    {
      id: PLANS.PLUS,
      name: PLAN_PRICING[PLANS.PLUS].name,
      price: PLAN_PRICING[PLANS.PLUS].price,
      displayPrice: PLAN_PRICING[PLANS.PLUS].displayPrice,
      interval: PLAN_PRICING[PLANS.PLUS].interval,
      features: [
        'Everything in Basic',
        'Cancellation guides',
        'Money saved tracking',
        'Advanced analytics & charts',
        'AI Insights',
        'Advanced AI classification',
        'Up to 5 Gmail accounts'
      ],
      recommended: true
    }
  ];
};

export default {
  PLANS,
  PLAN_PRICING,
  PLAN_LIMITS,
  PLAN_FEATURES,
  getUserPlan,
  updateUserPlan,
  adminOverridePlan,
  hasFeatureAccess,
  getUserFeatures,
  getUserPlanLimits,
  canAddGmailAccount,
  getPlanPricing,
  getAllPlans
};
